import React from 'react';
import styled from 'styled-components';
import { DotFillIcon, KebabHorizontalIcon } from '@primer/octicons-react';
import { Octicon } from '@primer/react';
import { LTText, LTProgressBar } from '@lambdatestincprivate/lt-components';
import { buildsColorMap, formatType } from './constants';
import MetricTile from './MetricTile';

// Ported from analytics InsightsReport/SummaryCard (Tailwind -> styled-components, Tippy -> a hover
// popover). Pass-rate card + status breakdown + 4 metric tiles.

const OTHERS_COLOR = '#666666';

const Wrap = styled.div`
  display: flex;
  flex-direction: ${({ $stacked }) => ($stacked ? 'column' : 'row')};
  gap: 16px;
  width: 100%;
`;

const Half = styled.div`
  width: ${({ $stacked }) => ($stacked ? '100%' : '50%')};
  min-width: 0;
`;

const RateCard = styled.div`
  width: 100%;
  min-height: 80px;
  padding: 12px 16px;
  background: #ffffff;
  border: 1px solid #d0d7de;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const RateNumber = styled.div`
  flex-shrink: 0;
  text-align: left;
  .pct {
    font-size: 20px;
    font-weight: 600;
    color: #24292f;
  }
`;

const RateBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 400;
`;

const KebabWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  cursor: default;
  &:hover .others-popover,
  &:focus-within .others-popover {
    visibility: visible;
    opacity: 1;
  }
`;

// White popover (matches the analytics build-insights Tippy card) listing the remaining statuses,
// each with its status color dot — instead of LTTooltip, whose text prop is string-only (no dots).
const OthersPopover = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 6px;
  z-index: 20;
  background: #ffffff;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(27, 31, 36, 0.12), 0 8px 24px rgba(66, 74, 83, 0.12);
  padding: 8px 12px;
  white-space: nowrap;
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.12s ease;
`;

const OtherRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #24292f;
  & + & {
    margin-top: 4px;
  }
`;

const Tiles = styled.div`
  display: flex;
  gap: 16px;
  min-width: 0;
  width: 100%;
`;

const computePassRate = (passed, total) => {
  if (!total) return 0;
  const r = (passed / total) * 100;
  return Number.isInteger(r) ? r : Math.round(r * 10) / 10;
};

const SummaryCard = ({ successRateData, stacked = false }) => {
  // No data yet => still loading the testSummary. Show dashes instead of fake 0s.
  const loading = !successRateData;
  const latestStatus = successRateData?.latest_status ?? {};
  const totalTest = successRateData?.total_test ?? 0;

  const sorted = Object.entries(latestStatus)
    .filter(([, v]) => typeof v === 'number')
    .map(([key, value]) => ({ key, value }))
    .sort((a, b) => b.value - a.value);

  const top3 = sorted.slice(0, 3);
  const others = sorted.slice(3);
  const othersCount = others.reduce((s, i) => s + i.value, 0);
  const statusTotal = sorted.reduce((s, i) => s + i.value, 0) || totalTest;
  const passed = sorted.find((i) => i.key === 'passed')?.value ?? 0;
  const passRate = computePassRate(passed, totalTest);

  const segments = [
    ...top3.map(({ key, value }) => ({
      id: key,
      progress: statusTotal ? (value / statusTotal) * 100 : 0,
      style: { backgroundColor: buildsColorMap[key] || OTHERS_COLOR },
    })),
    ...(others.length
      ? [
          {
            id: 'others',
            progress: statusTotal ? (othersCount / statusTotal) * 100 : 0,
            style: { backgroundColor: OTHERS_COLOR },
          },
        ]
      : []),
  ].filter((s) => s.progress > 0);

  const tiles = [
    { title: 'Total Unique Tests', value: successRateData?.test_cases_count },
    { title: 'Total Tests', value: successRateData?.total_test },
    { title: 'Total Instances', value: successRateData?.total_instances },
    { title: 'Total Retries', value: successRateData?.total_retries },
  ];

  return (
    <Wrap $stacked={stacked}>
      <Half $stacked={stacked}>
        <RateCard>
          <RateNumber>
            <div className="pct">{loading ? '-' : `${passRate}%`}</div>
            <LTText text="Pass Rate" color="#636C76" variant={LTText.TextVariant.SUBHEADER_BOLD} />
          </RateNumber>
          <RateBody>
            <Legend>
              {top3.map(({ key, value }) => (
                <LegendItem key={key}>
                  <DotFillIcon size={12} fill={buildsColorMap[key] || OTHERS_COLOR} />
                  <span>
                    <span>{value}</span> {formatType(key)}
                  </span>
                </LegendItem>
              ))}
              {others.length > 0 && (
                <KebabWrap aria-label="More statuses" tabIndex={0}>
                  <Octicon color={OTHERS_COLOR} icon={KebabHorizontalIcon} />
                  <OthersPopover className="others-popover">
                    {others.map(({ key, value }) => (
                      <OtherRow key={key}>
                        <DotFillIcon
                          size={12}
                          fill={buildsColorMap[key] || OTHERS_COLOR}
                        />
                        <span>
                          {formatType(key)}: {value}
                        </span>
                      </OtherRow>
                    ))}
                  </OthersPopover>
                </KebabWrap>
              )}
            </Legend>
            <LTProgressBar
              ariaLabel={`Progress of ${statusTotal} total tests`}
              segments={segments}
            />
          </RateBody>
        </RateCard>
      </Half>
      <Half $stacked={stacked}>
        <Tiles>
          {tiles.map((t) => (
            <MetricTile key={t.title} {...t} />
          ))}
        </Tiles>
      </Half>
    </Wrap>
  );
};

export default SummaryCard;
